import { CommonModule } from '@angular/common';
import { Component, OnDestroy, Signal, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { OrderService } from '../../order.service';
import { ModalContentService } from '../modal-content.service';
import { ModalContentTypes } from '../../constants/modal-content-types';
import {
  DiscountType,
  Promotion,
  PromotionRequirementIcon,
  PromotionView,
} from '../../types/promotion.type';
import { PromotionsService } from '../../promotions/promotions.service';
import { QuickAddService } from '../../quick-add.service';
import { SearchSuggestion } from '../../typesense-search.service';
import { AutocompleteProductCardComponent } from '../../header/search-bar/autocomplete-product-card/autocomplete-product-card.component';
import {
  buildPromotionView,
  findAppliedCouponFor,
  getConflictingDiscounts,
  getPromotionTitle,
  isCouponPromotion,
  mapOrderToPromotionState,
  sortPromotionsByClosest,
} from '../../promotions/promotions.util';

/**
 * Available promotions modal (DEV-9447).
 *
 * Lists the cart promotions the customer is eligible to see, shows applied ones
 * highlighted, lets them select an eligible promotion or remove an applied one,
 * and surfaces the stacking/override warnings. Manual promo codes can also be
 * entered here (stacking rules apply uniformly).
 */
@Component({
  selector: 'app-available-promotions',
  standalone: true,
  imports: [CommonModule, FormsModule, AutocompleteProductCardComponent],
  templateUrl: './available-promotions.component.html',
  styleUrl: './available-promotions.component.scss',
})
export class AvailablePromotionsComponent implements OnDestroy {
  private orderService = inject(OrderService);
  private modalContentService = inject(ModalContentService);
  private promotionsService = inject(PromotionsService);
  private quickAddService = inject(QuickAddService);
  private router = inject(Router);
  private activeModal = inject(NgbActiveModal);

  /** Expanded-card id currently being quick-added (drives the card spinner). */
  addingProductId = this.quickAddService.addingProductId;
  readonly DiscountType = DiscountType;

  promoCode = signal('');

  // Per-promotion product list (lazy): which promotion is expanded, its fetched
  // products, and the in-flight fetch id.
  expandedProductsId = signal<number | string | null>(null);
  productsLoadingId = signal<number | string | null>(null);
  private productsCache = signal<Map<number | string, SearchSuggestion[]>>(new Map());

  expandedProducts = computed(() => {
    const id = this.expandedProductsId();
    if (id == null) return [];
    return this.productsCache().get(id) || [];
  });

  order = computed(() => this.orderService.order());
  orderId = computed(() => this.order()?.id);

  isUpdating: Signal<boolean> = computed(() => this.orderService.promotionLoadingId() !== null);
  loadingId = computed(() => this.orderService.promotionLoadingId());
  errorMessage = computed(() => this.orderService.promotionErrorMessage());

  showCouponError = computed(() => this.orderService.showCouponErrorMessage());
  couponErrorMessage = computed(() => this.orderService.couponErrorMessage());

  /** Coupons already applied to the order (manual codes), removable from here. */
  appliedCoupons = computed(() => this.order()?.paymentDetails?.coupons || []);
  couponRemovingId = computed(() => this.orderService.couponRemovingId());

  /** Coupons confirmed for removal, awaiting the undo grace window. */
  pendingRemovalIds = signal<Set<number | string>>(new Set());
  /** Per-coupon countdown (seconds) shown on the Undo button. */
  pendingRemovalSeconds = signal<Map<number | string, number>>(new Map());
  private removalTimers = new Map<number | string, ReturnType<typeof setInterval>>();
  /** Seconds the user has to undo a coupon removal before it is applied. */
  readonly undoGraceSeconds = 5;

  private appliedPromotions = computed(() =>
    (this.order()?.promotions || []).filter((p: Promotion) => p.isApplied),
  );
  // Only non-stackable coupons can conflict with a non-stackable promotion;
  // a stackable coupon always combines (Stackable + Non-stackable = Accept).
  private nonStackableCouponCount = computed(
    () => (this.order()?.paymentDetails?.coupons || []).filter((c: any) => !c.isStackable).length,
  );

  /** View-models for every promotion, sorted closest-to-qualifying first. */
  promotionViews: Signal<PromotionView[]> = computed(() => {
    const order = this.order();
    const promotions: Promotion[] = order?.promotions || [];
    const state = mapOrderToPromotionState(order);
    return sortPromotionsByClosest(promotions, state).map((p) => buildPromotionView(p, state));
  });

  trackByPromotion = (_: number, view: PromotionView) => view.promotion.id;

  /** Material symbol name for a structured requirement row. */
  requirementIcon(icon: PromotionRequirementIcon): string {
    switch (icon) {
      case PromotionRequirementIcon.ORDER_MINIMUM:
        return 'payments';
      case PromotionRequirementIcon.QUANTITY:
        return 'shopping_basket';
      case PromotionRequirementIcon.CATEGORIES:
        return 'category';
      case PromotionRequirementIcon.TAGS:
        return 'sell';
      case PromotionRequirementIcon.PRODUCTS:
      default:
        return 'inventory_2';
    }
  }

  hasProductsCondition(promotion: Promotion): boolean {
    return this.promotionsService.hasProductSuggestions(promotion);
  }

  /** "View all in {category}" shop link for category-based promotions (else null). */
  categoryLink(promotion: Promotion) {
    return this.promotionsService.getPromotionCategoryLink(promotion);
  }

  goToCategory(promotion: Promotion): void {
    const link = this.categoryLink(promotion);
    if (!link) return;
    this.activeModal.close(null);
    this.router.navigateByUrl(link.url);
  }

  /** Whether an applied `paymentDetails.coupons[]` entry is a promotion (vs a coupon). */
  isPromotionEntry(coupon: any): boolean {
    return coupon?.type === DiscountType.PROMOTION;
  }

  /** Lazily loads (and caches) the products a promotion requires, toggling the list. */
  async toggleProducts(promotion: Promotion): Promise<void> {
    if (this.expandedProductsId() === promotion.id) {
      this.expandedProductsId.set(null);
      return;
    }

    if (this.productsCache().has(promotion.id)) {
      this.expandedProductsId.set(promotion.id);
      return;
    }

    this.productsLoadingId.set(promotion.id);
    const products = await this.promotionsService.getPromotionProducts(promotion);
    this.productsCache.update((cache) => new Map(cache).set(promotion.id, products));
    this.productsLoadingId.set(null);
    this.expandedProductsId.set(promotion.id);
  }

  /** Opening a product navigates to its detail page (closing the promotions modal). */
  onProductClick(product: SearchSuggestion): void {
    this.#goToProduct(product);
  }

  /**
   * The card's add button quick-adds the exact variant in place (DEV-9447), reusing
   * the shared search-bar flow via {@link QuickAddService}. Falls back to the PDP
   * only when quick-add isn't possible (needs variant selection / OOS / anonymous),
   * closing the modal on navigation.
   */
  onProductAddToCart(product: SearchSuggestion): void {
    this.quickAddService.addOrNavigate(product, {
      beforeNavigate: () => this.activeModal.close(null),
    });
  }

  #goToProduct(product: SearchSuggestion): void {
    if (!product?.productUrl) return;
    this.activeModal.close(null);
    this.router.navigateByUrl(`/shop/${product.productUrl}`);
  }

  couponId(coupon: any): number | string {
    return coupon?.id ?? coupon?.code;
  }

  couponLabel(coupon: any): string {
    const fallback = this.isPromotionEntry(coupon) ? 'Promotion' : 'Coupon';
    return coupon?.discount || coupon?.benefit || coupon?.code || coupon?.name || fallback;
  }

  isPendingRemoval(coupon: any): boolean {
    return this.pendingRemovalIds().has(this.couponId(coupon));
  }

  /** Seconds left to undo the removal of a coupon (0 when none). */
  pendingSeconds(coupon: any): number {
    return this.pendingRemovalSeconds().get(this.couponId(coupon)) ?? 0;
  }

  /** Asks for confirmation; on accept, starts the undo grace window. */
  requestRemoveCoupon(coupon: any): void {
    if (this.couponRemovingId() != null || this.isPendingRemoval(coupon)) return;

    const isPromotion = this.isPromotionEntry(coupon);
    const textContent = isPromotion
      ? `Are you sure you want to remove “${this.couponLabel(coupon)}” from your order?`
      // Neutral on purpose (DEV-11691, to validate with the client): the old copy promised it
      // would never show here again, the opposite of what was asked. Re-entering the code
      // works whether or not the backend lists the coupon under "Available promotions" yet.
      : `Are you sure you want to remove “${this.couponLabel(coupon)}” from your order? You can apply it again later with the same coupon code.`;

    this.modalContentService
      .openModal(ModalContentTypes.CONFIRMATION, {
        title: isPromotion ? 'Remove this promotion?' : 'Remove this coupon?',
        textContent,
        cancelButtonText: 'Cancel',
        confirmButtonText: 'Remove',
      })
      .closed.subscribe((res: { confirm: boolean } | null) => {
        if (res?.confirm) this.#startPendingRemoval(coupon);
      });
  }

  /** Cancels a pending removal during the grace window. */
  undoRemoveCoupon(coupon: any): void {
    this.#clearPending(this.couponId(coupon));
  }

  #startPendingRemoval(coupon: any): void {
    const id = this.couponId(coupon);
    this.#setPending(id, true);
    this.#setSeconds(id, this.undoGraceSeconds);

    const timer = setInterval(() => {
      const remaining = this.pendingSeconds(coupon) - 1;
      if (remaining <= 0) {
        this.#finalizeRemoval(coupon);
      } else {
        this.#setSeconds(id, remaining);
      }
    }, 1000);
    this.removalTimers.set(id, timer);
  }

  #finalizeRemoval(coupon: any): void {
    const id = this.couponId(coupon);
    this.#clearPending(id);
    const orderId = this.orderId();
    if (!orderId) return;
    this.orderService.removeCoupon(coupon, +orderId).subscribe();
  }

  #clearPending(id: number | string): void {
    const timer = this.removalTimers.get(id);
    if (timer) clearInterval(timer);
    this.removalTimers.delete(id);
    this.#setPending(id, false);
    this.#setSeconds(id, null);
  }

  #setPending(id: number | string, pending: boolean): void {
    this.pendingRemovalIds.update((set) => {
      const next = new Set(set);
      if (pending) next.add(id);
      else next.delete(id);
      return next;
    });
  }

  #setSeconds(id: number | string, seconds: number | null): void {
    this.pendingRemovalSeconds.update((map) => {
      const next = new Map(map);
      if (seconds == null) next.delete(id);
      else next.set(id, seconds);
      return next;
    });
  }

  ngOnDestroy(): void {
    // Apply any confirmed-but-pending removals so closing the modal doesn't lose
    // the user's intent, and clear timers to avoid leaks.
    const coupons = this.appliedCoupons();
    const orderId = this.orderId();
    this.removalTimers.forEach((timer, id) => {
      clearInterval(timer);
      const coupon = coupons.find((c: any) => this.couponId(c) === id);
      if (coupon && orderId) this.orderService.removeCoupon(coupon, +orderId).subscribe();
    });
    this.removalTimers.clear();
  }

  applyPromoCode(): void {
    if (this.isUpdating() || !this.promoCode().trim()) return;
    const orderId = this.orderId();
    if (!orderId) return;
    // Reuse the order coupon flow; the backend applies the same stacking rules.
    const codeSignal = signal(this.promoCode());
    this.orderService.applyCouponWithOrder(codeSignal, +orderId).subscribe(() => {
      this.promoCode.set('');
    });
  }

  /** Triggered by the "Select" button on an eligible promotion. */
  select(view: PromotionView): void {
    if (!view.isMet || this.isUpdating()) return;
    const promotion = view.promotion;

    // A coupon card sends its code again (DEV-11691). No override warning: the coupon
    // endpoint has no override, and a clash comes back as the STACKING_CONFLICT copy, which
    // already tells the shopper what to remove — the same as typing the code.
    if (isCouponPromotion(promotion) && promotion.couponCode) {
      const orderId = this.orderId();
      if (orderId) this.orderService.applyCouponPromotion(promotion, +orderId).subscribe();
      return;
    }

    const conflict = getConflictingDiscounts(
      promotion,
      this.appliedPromotions(),
      this.nonStackableCouponCount(),
    );

    if (conflict.hasConflict) {
      this.#openOverrideWarning(promotion);
      return;
    }

    this.#apply(promotion);
  }

  /**
   * Triggered by removing an applied promotion. Always confirms first (DEV-9447):
   * automatic promotions warn they can't be restored; manual ones get a generic
   * confirmation.
   */
  remove(promotion: Promotion): void {
    if (this.isUpdating()) return;

    // Before the automatic check: a coupon card keeps coming back after removal (DEV-11691),
    // so "it cannot be restored" would not be true for it.
    if (isCouponPromotion(promotion)) {
      this.#openManualRemovalConfirmation(promotion);
      return;
    }

    if (promotion.isAutomatic) {
      this.#openAutomaticRemovalWarning(promotion);
      return;
    }

    this.#openManualRemovalConfirmation(promotion);
  }

  #apply(promotion: Promotion, override = false): void {
    const orderId = this.orderId();
    if (!orderId) return;
    this.orderService.applyPromotion(promotion.id, +orderId, override).subscribe();
  }

  #remove(promotion: Promotion): void {
    const orderId = this.orderId();
    if (!orderId) return;

    // A coupon card removes its `paymentDetails.coupons[]` entry, exactly as the "Applied
    // promotions" list does (DEV-11691). Without a match it falls back to the card's own id.
    const coupon = isCouponPromotion(promotion)
      ? findAppliedCouponFor(promotion, this.appliedCoupons())
      : undefined;
    const discount = coupon
      ? { id: this.couponId(coupon), type: DiscountType.COUPON }
      : undefined;
    this.orderService.removePromotion(promotion.id, +orderId, discount).subscribe();
  }

  #openOverrideWarning(promotion: Promotion): void {
    this.modalContentService
      .openModal(ModalContentTypes.CONFIRMATION, {
        title: 'Override existing promotion?',
        // Verbatim from the client (DEV-10937 point 6). One wording for every conflict,
        // including a manually entered coupon: the screen calls all of them promotions, so
        // a warning that suddenly said "coupon" would contradict the card the shopper just
        // clicked. `conflictsWithCoupon` still decides WHETHER we warn, just not the words.
        textContent:
          'Applying this promotion will override your existing promotion. ' +
          'You can re-apply the promotion later if you change your mind.',
        cancelButtonText: 'Keep current',
        confirmButtonText: 'Apply promotion',
      })
      .closed.subscribe((res: { confirm: boolean } | null) => {
        // Applied WITH override: the shopper has just agreed to lose the current promotion,
        // and a plain apply would come back as STACKING_CONFLICT with it still on.
        if (res?.confirm) this.#apply(promotion, true);
      });
  }

  #openManualRemovalConfirmation(promotion: Promotion): void {
    this.modalContentService
      .openModal(ModalContentTypes.CONFIRMATION, {
        title: 'Remove this promotion?',
        textContent: `Are you sure you want to remove “${getPromotionTitle(promotion)}” from your order?`,
        cancelButtonText: 'Cancel',
        confirmButtonText: 'Remove',
      })
      .closed.subscribe((res: { confirm: boolean } | null) => {
        if (res?.confirm) this.#remove(promotion);
      });
  }

  #openAutomaticRemovalWarning(promotion: Promotion): void {
    this.modalContentService
      .openModal(ModalContentTypes.CONFIRMATION, {
        title: 'Remove this promotion?',
        textContent:
          'If you remove this promotion it will be lost and cannot be restored later. Are you sure?',
        cancelButtonText: 'Cancel',
        confirmButtonText: 'Remove',
      })
      .closed.subscribe((res: { confirm: boolean } | null) => {
        if (res?.confirm) this.#remove(promotion);
      });
  }
}
